import React, { useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  Layout,
  Body,
  Card,
  Heading,
  FormInput,
  FormDescription,
  Label,
  SubmitButton,
  Text,
  PageHead,
} from '@/components/Index';
import { forgotPassword } from '@/services/index';

const ShowError = dynamic(() => import('@/components/ShowError'));

const forgotFormSchema = yup.object().shape({
  email: yup.string().email().required('Email is required.'),
});

export default function ForgotPassword() {
  const [responseError, setResponseError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(forgotFormSchema) });

  const onFormSubmit = (data) => {
    forgotPassword(data)
      .then((res) => {
        handleSuccess(res);
      })
      .catch((e) => {
        handleFailure(e);
      });
  };

  const handleSuccess = () => {
    setShowSuccess(true);
  };
  const handleFailure = (err) => {
    setResponseError(err);
  };

  return (
    <>
      <PageHead
        title="Forgot password?"
        description="Liftoff Forgot Password page."
      />
      <Layout>
        <Body>
          {!showSuccess && (
            <Card dataTestId="forgot-section">
              <Heading text="Forgot your password?" />
              <FormDescription
                content="
                Don't worry, it happens."
              />
              <form onSubmit={handleSubmit(onFormSubmit)}>
                <div className="mt-32">
                  <Label htmlFor="email" text="Email" />
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <FormInput
                        id="email"
                        name="email"
                        type="email"
                        value={field.value}
                        isRequired={true}
                        placeholder="Enter email"
                        dataTestId="user-email"
                        isErrored={!!errors.email}
                        errorMessages={errors.email?.message}
                        onChange={(value) => {
                          setValue('email', value, {
                            shouldValidate: true,
                          });
                        }}
                      />
                    )}
                  />
                </div>
                {responseError && (
                  <div className="mt-16">
                    <ShowError err={responseError} />
                  </div>
                )}
                <div className="mt-32">
                  <SubmitButton value="Email Recovery Link" />
                </div>
              </form>
              <div className="mt-32">
                <Text>
                  Remember your password?
                  <Link href="/login" data-testid="login-link">
                    {' '}
                    Log in
                  </Link>
                </Text>
              </div>
              <div className="mt-16">
                <Text>
                  Don&apos;t have an account yet?
                  <Link href="/signup" data-testid="signup-link">
                    {' '}
                    Sign up
                  </Link>
                </Text>
              </div>
            </Card>
          )}
          {showSuccess && <SuccessForgot />}
        </Body>
      </Layout>
    </>
  );
}

function SuccessForgot() {
  return (
    <div className="flex flex-col justify-center" data-testid="success-section">
      <Card>
        <Heading text="Check your email!" />
        <div className="mt-16">
          <Text>
            If you have an account with an associated password, we will send you
            instructions to reset your password. For employee accounts, please
            contact IT instead.
          </Text>
        </div>
      </Card>
    </div>
  );
}
